import apiClient from './AuthService';

export interface VirtualSpacesChallenge {
  virtual_spaces_challenge_id?: string;
  project_id: string;
  user_id?: string;
  member_id: string;
  image_url?: string;
  comment?: string;
  points?: string;
  cozy?: number;
  spacious?: number;
  bright?: number;
  created_at?: string;
  updated_at?: string;
}

export interface VirtualSpacesChallengeResponse {
  virtual_spaces_challenge_id: string;
  message: string;
}

export interface VirtualSpacesChallengeData {
  data: VirtualSpacesChallenge | VirtualSpacesChallenge[];
}

// Structure for storing all 10 images data
export interface ImageResponse {
  imageIndex: number;
  imagePath: string;
  text: string;
  annotations: Array<{x: number; y: number; id: string; label: string}>;
}

export interface AllImagesData {
  responses: ImageResponse[];
  completed: boolean;
  completedAt?: string;
  together?: boolean;
}

export const saveVirtualSpacesChallenge = async (
  projectId: string,
  memberId: string,
  imageIndex: number,
  imageText: string,
  annotations: Array<{x: number; y: number; id: string; label: string}>
): Promise<VirtualSpacesChallengeResponse> => {
  try {
    // Generate image path
    const imagePath = `/spaces/image${String(imageIndex).padStart(2, '0')}.jpg`;

    // Log the data being sent
    const requestData = {
      project_id: projectId,
      member_id: memberId,
      image_url: imagePath, // Actual image path
      comment: imageText || '', // User's text only, no JSON
      points: JSON.stringify(annotations || []), // Coordinates as JSON string
      cozy: 0,
      spacious: 0,
      bright: 0
    };

    console.log('📤 Sending VirtualSpaces data for image', imageIndex, ':', requestData);

    const response = await apiClient.post<VirtualSpacesChallengeResponse>(
      '/quiz/virtual-spaces-challenge',
      requestData
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ VirtualSpaces API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    // Extract error message properly
    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message && typeof error.response.data.message === 'string') {
      throw new Error(error.response.data.message);
    } else if (error.response?.data && typeof error.response.data === 'string') {
      throw new Error(error.response.data);
    } else if (error.response?.data) {
      // If data is an object, try to extract meaningful info
      console.error('Error response data is an object:', error.response.data);
      throw new Error(JSON.stringify(error.response.data));
    }

    throw new Error('Error al guardar el challenge de espacios virtuales. Por favor, intenta de nuevo.');
  }
};

export const getVirtualSpacesChallenge = async (
  virtualSpacesChallengeId: string
): Promise<VirtualSpacesChallenge> => {
  try {
    const response = await apiClient.get<VirtualSpacesChallengeData>(
      `/quiz/virtual-spaces-challenge/${virtualSpacesChallengeId}`
    );
    return response.data.data as VirtualSpacesChallenge;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getProjectVirtualSpacesChallenges = async (
  projectId: string
): Promise<VirtualSpacesChallenge[]> => {
  try {
    const response = await apiClient.get<VirtualSpacesChallengeData>(
      `/quiz/project/${projectId}/virtual-spaces-challenges`
    );
    return response.data.data as VirtualSpacesChallenge[];
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getMemberVirtualSpacesChallenges = async (
  memberId: string
): Promise<VirtualSpacesChallenge[]> => {
  try {
    console.log('📡 Fetching virtual spaces challenges for member:', memberId);
    const response = await apiClient.get<VirtualSpacesChallengeData>(
      `/quiz/member/${memberId}/virtual-spaces-challenges`
    );
    console.log('📡 Response received:', response.data);

    // Handle both wrapped and direct response formats
    const data = response.data.data || response.data;

    // If data is not an array, wrap it
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }

    return data as VirtualSpacesChallenge[];
  } catch (error: any) {
    console.error('❌ getMemberVirtualSpacesChallenges error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    // Return empty array for 404 (no data found)
    if (error.response?.status === 404) {
      console.log('📡 No challenges found for member (404), returning empty array');
      return [];
    }

    // For other errors, log and throw
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

export const checkMemberHasCompletedChallenge = async (
  memberId: string
): Promise<boolean> => {
  try {
    const challenges = await getMemberVirtualSpacesChallenges(memberId);
    if (challenges && challenges.length > 0) {
      // Check if the challenge is completed by parsing the comment field
      const challenge = challenges[0];
      if (challenge.comment) {
        try {
          const data = JSON.parse(challenge.comment) as AllImagesData;
          return data.completed === true;
        } catch {
          return false;
        }
      }
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const getExistingResponses = async (
  memberId: string
): Promise<AllImagesData | null> => {
  try {
    console.log('📦 getExistingResponses: Starting for member', memberId);
    const challenges = await getMemberVirtualSpacesChallenges(memberId);
    console.log('📦 getExistingResponses: Received', challenges?.length || 0, 'challenges');

    if (challenges && challenges.length > 0) {
      // Now we have multiple records, one per image
      const responses: ImageResponse[] = challenges.map(challenge => {
        // Extract image index from image_url (e.g., "/spaces/image01.jpg" -> 1)
        const match = challenge.image_url?.match(/image(\d+)\.jpg/);
        const imageIndex = match ? parseInt(match[1], 10) : 0;

        // Parse annotations from points field
        let annotations = [];
        if (challenge.points) {
          try {
            annotations = JSON.parse(challenge.points);
          } catch (e) {
            console.warn('📦 Failed to parse points for challenge:', challenge.virtual_spaces_challenge_id, e);
            annotations = [];
          }
        }

        const wordCount = challenge.comment?.trim().split(/\s+/).filter(w => w.length > 0).length || 0;
        console.log(`📦 Image ${imageIndex}: ${wordCount} words, ${annotations.length} annotations`);

        return {
          imageIndex,
          imagePath: challenge.image_url || '',
          text: challenge.comment || '', // Now comment contains just the text
          annotations
        };
      });

      // Sort by image index
      responses.sort((a, b) => a.imageIndex - b.imageIndex);

      console.log('📦 getExistingResponses: Returning', responses.length, 'sorted responses');

      // Return in the expected format
      return {
        responses,
        completed: responses.length === 10, // Assuming 10 images total
        completedAt: challenges[0].created_at,
        together: true
      };
    }

    console.log('📦 getExistingResponses: No challenges found, returning null');
    return null;
  } catch (error: any) {
    console.error('📦 getExistingResponses error:', error.message || error);
    // Don't throw, just return null to indicate no data
    return null;
  }
};